import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import { ToastService } from '../toast/toast.service';
import { Router } from '@angular/router';

export interface UserProfile {
  email?: string;
  full_name?: string;
  id?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  auth_provider?: string; // 'google', 'email', etc.
  provider?: string; // Alternative field name for auth provider
  is_google_user?: boolean; // Boolean flag for Google users
  google_id?: string | null; // Google ID if user logged in via Google
}

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  private userProfile$ = new BehaviorSubject<UserProfile | null>(null);
  private loading$ = new BehaviorSubject<boolean>(false);
  private fetchInProgress = false;
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  private logoutInProgress = false; // Prevent multiple logout attempts

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Get user profile as Observable
   */
  getUserProfile(): Observable<UserProfile | null> {
    return this.userProfile$.asObservable();
  }

  /**
   * Get current user profile value (synchronous)
   */
  getCurrentProfile(): UserProfile | null {
    return this.userProfile$.value;
  }

  /**
   * Get loading state
   */
  isLoading(): Observable<boolean> {
    return this.loading$.asObservable();
  }

  /**
   * Fetch user profile from API (with caching and duplicate request prevention).
   * @param skipGlobalLoader Skip fullscreen loader (e.g. on Settings while other panels show local loading).
   */
  fetchUserProfile(
    forceRefresh: boolean = false,
    skipGlobalLoader: boolean = false,
  ): Observable<UserProfile | null> {
    if (!this.isBrowser()) {
      return of(null);
    }

    if (!this.authService.isAuthenticated()) {
      this.userProfile$.next(null);
      return of(null);
    }

    // Check if we have cached data and it's still valid
    const now = Date.now();
    const cachedProfile = this.userProfile$.value;
    if (!forceRefresh && cachedProfile && now - this.lastFetchTime < this.CACHE_DURATION) {
      return of(cachedProfile);
    }

    // Prevent duplicate requests
    if (this.fetchInProgress) {
      return this.userProfile$.asObservable();
    }

    this.fetchInProgress = true;
    this.loading$.next(true);

    return this.apiService.get<UserProfile>('/auth/me', {}, false, skipGlobalLoader).pipe(
      tap({
        next: (response: any) => {
          this.fetchInProgress = false;
          this.loading$.next(false);
          this.lastFetchTime = Date.now();

          // Handle different response formats
          let profile: UserProfile | null = null;
          if (response?.data) {
            profile = response.data;
          } else if (response?.email || response?.full_name) {
            profile = response;
          } else if (response) {
            profile = response;
          }

          this.userProfile$.next(profile);
        },
        error: () => {
          this.fetchInProgress = false;
          this.loading$.next(false);
          // Don't clear existing profile on error, keep cached data
        },
      }),
      catchError((error: any) => {
        this.fetchInProgress = false;
        this.loading$.next(false);

        // Only treat as token expiration if we get a clear indication
        // Don't log out on every 401 - it might be a temporary issue
        const errorMessage =
          error?.message ||
          error?.error?.detail ||
          error?.error?.error ||
          error?.error?.message ||
          '';
        const errorMessageLower = errorMessage.toLowerCase();
        const status = error?.status || 0;

        // More specific checks - only log out if we get explicit token expiration messages
        // Not just any 401 error
        const isTokenExpired =
          status === 401 &&
          (errorMessageLower.includes('invalid or expired token') ||
            errorMessageLower.includes('expired token') ||
            errorMessageLower.includes('token has expired') ||
            errorMessageLower.includes('token expired') ||
            errorMessageLower.includes('please login again') ||
            errorMessageLower.includes('session expired') ||
            errorMessageLower.includes('authentication required') ||
            errorMessageLower.includes('unauthorized') ||
            errorMessageLower.includes('not authenticated'));

        // Only log out if we have a clear token expiration AND user is still authenticated
        // AND we haven't already initiated a logout
        // This prevents logging out on temporary network issues
        if (
          isTokenExpired &&
          this.isBrowser() &&
          this.authService.isAuthenticated() &&
          !this.logoutInProgress
        ) {
          // Double-check: verify token is actually invalid by checking if it exists
          const token = this.authService.getToken();
          if (!token) {
            // Token doesn't exist, definitely expired
            this.handleSessionExpiration();
          } else {
            // Token exists but API says it's expired - log out
            this.handleSessionExpiration();
          }
        } else if (status === 401 && !isTokenExpired) {
          // 401 but not a clear token expiration - might be a temporary issue
          // Don't log out, just keep cached profile
          console.warn(
            'Received 401 but not a clear token expiration. Keeping user logged in. Error:',
            errorMessage,
          );
        } else if (status === 0) {
          // Network error - don't log out, just keep cached profile
          console.warn('Network error while fetching profile. Keeping user logged in.');
        }

        return of(this.userProfile$.value);
      }),
      shareReplay(1),
    );
  }

  /**
   * Clear user profile (e.g., on logout)
   */
  clearProfile(): void {
    this.userProfile$.next(null);
    this.lastFetchTime = 0;
    this.fetchInProgress = false;
  }

  /**
   * Force refresh user profile (bypass cache).
   * @param skipGlobalLoader Omit fullscreen overlay (Settings uses local loading state instead).
   */
  refreshProfile(skipGlobalLoader = false): Observable<UserProfile | null> {
    return this.fetchUserProfile(true, skipGlobalLoader);
  }

  /**
   * Handle session expiration - centralized logout logic
   */
  private handleSessionExpiration(): void {
    // Prevent multiple logout attempts
    if (this.logoutInProgress) {
      return;
    }

    this.logoutInProgress = true;

    // Show toaster notification
    this.toastService.error('Session expired please login to continue.', 'Session Expired');

    // Clear authentication and profile
    this.authService.logout();
    this.clearProfile();

    // Redirect to login page
    setTimeout(() => {
      this.router.navigate(['/login'], { replaceUrl: true });
      this.logoutInProgress = false; // Reset flag after navigation
    }, 1000);
  }
}
